import { FC, useState, useCallback, useRef, useEffect } from 'react';
import { KatButton, KatToggle } from '@amzn/katal-react';
import CropIcon from '@mui/icons-material/Crop';
import {
  DividerStyle,
  MarketPlace,
  TransformationVariant,
} from '@amzn/fire-tv-campaign-automation-service';
import {
  MultiImageInput,
  ImageEntry,
  ImageValidationSpec,
} from 'src/components/general/MultiImageInput';
import { CropModal } from 'src/components/general/CropModal/CropModal';
import { OverlaySection } from './OverlaySection';
import { StyledDropdown } from 'src/components/general/StyledDropdown';
import { AssetState, AssetStatus } from 'src/types';
import { MULTI_TITLE_VERTICAL_CONSTRAINTS } from 'src/constants';
import { isFeatureEnabled, FeatureFlag } from 'src/featureFlags';
import styles from './MultiTitleVerticalInput.module.css';

export interface TileCrop {
  leftOffset: number;
  topOffset: number;
}

export interface MultiTitleVerticalInputProps {
  disabled: boolean;
  assetState: AssetState;
  marketplace: MarketPlace;
  setState: (state: Partial<AssetState>) => void;
  onGenerate: (images: ImageEntry[], options: MultiTitleVerticalOptions) => void;
  onCancel: () => void;
}

export interface MultiTitleVerticalOptions {
  dividerStyle: DividerStyle;
  leftOffsets?: number[];
  topOffsets?: number[];
  forceScreensaver?: boolean;
  enableSmartProcessing?: boolean;
}

const DIVIDER_OPTIONS = [
  { value: 'straight', name: 'Straight' },
  { value: 'slanted', name: 'Slanted' },
];

// Per-tile slot widths from backend template config (standard_configurations.json5)
// Keyed by divider style → image count → array of widths per tile position
const SLOT_WIDTHS: Record<DividerStyle, Record<number, number[]>> = {
  [DividerStyle.STRAIGHT]: {
    2: [1300, 620],
    3: [1074, 423, 423],
  },
  [DividerStyle.SLANTED]: {
    2: [1380, 695],
    3: [1250, 500, 420],
  },
};

const VALIDATION_SPEC: ImageValidationSpec = {
  maxFileSizeBytes: 10 * 1024 * 1024,
  minWidth: 1280,
  minHeight: 720,
  aspectRatio: { width: 16, height: 9 },
  acceptedFileTypes: MULTI_TITLE_VERTICAL_CONSTRAINTS.ACCEPTED_IMAGE_TYPES,
};

/**
 * Input component for the Multi Title Vertical transformation variant.
 * Supports 2-3 images with per-tile crop (keyed by image ID), divider style selection,
 * forceScreensaver toggle, and enableSmartProcessing toggle.
 */
export const MultiTitleVerticalInput: FC<MultiTitleVerticalInputProps> = ({
  disabled,
  assetState,
  marketplace,
  setState,
  onGenerate,
  onCancel,
}) => {
  const [images, setImages] = useState<ImageEntry[]>([]);
  const [dividerStyle, setDividerStyle] = useState<DividerStyle>(DividerStyle.STRAIGHT);
  const [forceScreensaver, setForceScreensaver] = useState(false);
  const [enableSmartProcessing, setEnableSmartProcessing] = useState(false);
  const [manualCropEnabled, setManualCropEnabled] = useState(false);
  // Crops keyed by image ID (not index) to survive reorder
  const [tileCrops, setTileCrops] = useState<Record<string, TileCrop>>({});
  const [cropTileIndex, setCropTileIndex] = useState<number | null>(null);
  const prevImagesRef = useRef<ImageEntry[]>([]);

  // Keep ref in sync with state
  useEffect(() => {
    prevImagesRef.current = images;
  }, [images]);

  const handleImagesChange = useCallback((newImages: ImageEntry[]) => {
    // Detect reorder: same IDs but different order → clear crops (slot widths changed)
    const oldIds = prevImagesRef.current.map((img) => img.id);
    const newIds = newImages.map((img) => img.id);
    const isReorder =
      oldIds.length === newIds.length &&
      oldIds.every((id) => newIds.includes(id)) &&
      oldIds.some((id, idx) => newIds[idx] !== id);

    if (isReorder) {
      setTileCrops({});
    } else {
      // Clean up crops for removed images only
      const validIds = new Set(newIds);
      setTileCrops((prev) => {
        const cleaned: Record<string, TileCrop> = {};
        for (const [id, crop] of Object.entries(prev)) {
          if (validIds.has(id)) cleaned[id] = crop;
        }
        return cleaned;
      });
    }

    setImages(newImages);
  }, []);

  const handleDividerChange = (e: { detail: { value: string } }) => {
    setDividerStyle(e.detail.value as DividerStyle);
    // Clear crops — slot widths differ between divider styles so existing crops are invalid
    setTileCrops({});
  };

  const handleCropSave = (tileIndex: number) => (state: Partial<AssetState>) => {
    const imgId = images[tileIndex]?.id;
    if (!imgId) return;
    setTileCrops((prev) => ({
      ...prev,
      [imgId]: {
        leftOffset: state.leftOffset ?? 0,
        topOffset: state.topOffset ?? 0,
      },
    }));
    setCropTileIndex(null);
  };

  const canGenerate =
    images.length >= MULTI_TITLE_VERTICAL_CONSTRAINTS.MIN_IMAGES &&
    images.length <= MULTI_TITLE_VERTICAL_CONSTRAINTS.MAX_IMAGES;

  // Derive offsets from crops (ordered by current image order)
  const deriveOffsets = (): { leftOffsets?: number[]; topOffsets?: number[] } => {
    const hasCrops = Object.keys(tileCrops).length > 0;
    if (!hasCrops) return {};
    const leftOffsets: number[] = [];
    const topOffsets: number[] = [];
    for (const img of images) {
      const crop = tileCrops[img.id];
      leftOffsets.push(crop?.leftOffset ?? 0);
      topOffsets.push(crop?.topOffset ?? 0);
    }
    return { leftOffsets, topOffsets };
  };

  const handleGenerate = () => {
    if (!canGenerate) return;
    const { leftOffsets, topOffsets } = deriveOffsets();
    onGenerate(images, {
      dividerStyle,
      leftOffsets,
      topOffsets,
      forceScreensaver: leftOffsets ? forceScreensaver : undefined,
      enableSmartProcessing: enableSmartProcessing || undefined,
    });
  };

  const overlayEnabled = assetState.status === AssetStatus.COMPLETED && !!assetState.key;

  // CropModal state
  const cropImageId = cropTileIndex !== null ? images[cropTileIndex]?.id : null;
  const cropImageState =
    cropImageId !== null
      ? ({
          leftOffset: tileCrops[cropImageId]?.leftOffset,
          topOffset: tileCrops[cropImageId]?.topOffset,
        } as AssetState)
      : ({} as AssetState);
  const cropImageKey = cropTileIndex !== null ? images[cropTileIndex]?.preview : undefined;

  return (
    <div className={styles.container} data-testid="multi-title-vertical-input">
      <MultiImageInput
        images={images}
        onImagesChange={handleImagesChange}
        maxImages={MULTI_TITLE_VERTICAL_CONSTRAINTS.MAX_IMAGES}
        validationSpec={VALIDATION_SPEC}
        disabled={disabled}
        reorderHint="Drag to reorder tiles. Order determines left-to-right position."
        data-testid="mtv-multi-image"
      />

      <div className={styles.controlRow}>
        <div className={styles.controlGroup}>
          <span className={styles.controlLabel}>Divider Style</span>
          <StyledDropdown
            options={DIVIDER_OPTIONS}
            value={dividerStyle}
            onChange={handleDividerChange}
            placeholder="Select divider style"
            data-testid="mtv-divider-style"
            disabled={disabled}
          />
        </div>
      </div>

      {/* Manual Crop Toggle */}
      <div className={styles.toggleRow}>
        <KatToggle
          checked={manualCropEnabled}
          onChange={() => {
            setManualCropEnabled((v) => !v);
            if (manualCropEnabled) {
              setTileCrops({});
              setForceScreensaver(false);
            }
          }}
          disabled={disabled || !canGenerate}
          data-testid="mtv-manual-crop-toggle"
        />
        <span className={styles.controlLabel}>Manual crop positioning</span>
      </div>

      {/* Per-tile Crop Section */}
      {manualCropEnabled && canGenerate && (
        <div className={styles.controlGroup}>
          <span className={styles.controlLabel}>Tile Preview</span>
          <div className={styles.tileGrid} data-testid="mtv-tile-preview-section">
            {images.map((img, idx) => (
              <div key={img.id} className={styles.tileCard} data-testid={`mtv-tile-card-${idx}`}>
                <img src={img.preview} alt={`Tile ${idx + 1}`} className={styles.tileThumbnail} />
                <span className={styles.tileBadge}>{tileCrops[img.id] ? 'Manual' : 'Auto'}</span>
                <KatButton
                  variant="secondary"
                  size="small"
                  onClick={() => setCropTileIndex(idx)}
                  disabled={disabled}
                  data-testid={`mtv-crop-tile-${idx}-button`}
                >
                  <CropIcon fontSize="small" /> Crop
                </KatButton>
              </div>
            ))}
          </div>
          <span className={styles.helperText}>
            Click Crop on any tile to manually position. Uncropped tiles use smart positioning.
          </span>

          {/* Force Screensaver (only when manual crop is active) */}
          <div className={styles.toggleRow}>
            <KatToggle
              checked={forceScreensaver}
              onChange={() => setForceScreensaver((v) => !v)}
              disabled={disabled}
              data-testid="mtv-force-screensaver-toggle"
            />
            <span className={styles.controlLabel}>Force screensaver composition for Tile 1</span>
          </div>
        </div>
      )}

      {/* Smart Processing Toggle */}
      <div className={styles.controlGroup}>
        <div className={styles.toggleRow}>
          <KatToggle
            checked={enableSmartProcessing}
            onChange={() => setEnableSmartProcessing(!enableSmartProcessing)}
            disabled={disabled}
            data-testid="mtv-smart-processing-toggle"
          />
          <span className={styles.controlLabel}>Enable Smart Processing</span>
        </div>
        <span className={styles.helperText}>
          Enables AI-driven image cleanup and subject protection. User review advised.
        </span>
      </div>

      {/* Overlay Controls */}
      {isFeatureEnabled(FeatureFlag.OverlaySection) && (
        <OverlaySection
          assetState={assetState}
          marketplace={marketplace}
          enabled={overlayEnabled}
          setState={setState}
        />
      )}

      {/* Action Buttons */}
      <div className={styles.actions}>
        <KatButton
          variant="tertiary"
          onClick={onCancel}
          disabled={disabled}
          data-testid="mtv-cancel-button"
        >
          Cancel
        </KatButton>
        <KatButton
          variant="primary"
          onClick={handleGenerate}
          disabled={disabled || !canGenerate}
          data-testid="mtv-generate-button"
        >
          Generate
        </KatButton>
      </div>

      {/* Per-tile CropModal — slot widths vary by tile position and layout */}
      {cropTileIndex !== null && cropImageKey && (
        <CropModal
          isVisible={true}
          onClose={() => setCropTileIndex(null)}
          imageState={cropImageState}
          updateAssetState={handleCropSave(cropTileIndex)}
          imageKey={cropImageKey}
          orientation="horizontal"
          keptSize={SLOT_WIDTHS[dividerStyle]?.[images.length]?.[cropTileIndex]}
          outputSize={1920}
          data-testid="mtv-tile-crop-modal"
        />
      )}
    </div>
  );
};
